"use client"

import { motion } from "motion/react"
import { ArrowRight } from "lucide-react"
import Link from "next/link"

import { Button } from "@/core/ui/button"
import TechBlackHole from "@/core/ui/effects/TechBlackHole"


export default function ServicesHero() {
  return (
    <div className="relative min-h-screen w-full flex items-center overflow-hidden bg-background transition-colors duration-300">
      {/* Background Layer - Black Hole positioned to right-top */}
      <div className="absolute inset-0 z-0 opacity-100 dark:opacity-100 opacity-30 mix-blend-multiply dark:mix-blend-normal">
        <TechBlackHole offsetX={1.2} offsetY={0.3} />
      </div>

      {/* Content Layer - Left/Center aligned */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mb-8"
          >
            <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 backdrop-blur-md text-xs font-medium tracking-[0.15em] text-cyan-500 dark:text-cyan-400 uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 dark:bg-cyan-400 mr-2 animate-pulse" />
              Engineering Services
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground mb-4 leading-[0.95]"
          >
            Turnkey AI &amp;
          </motion.h1>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8 leading-[0.95]"
          >
            <span className="bg-clip-text text-transparent bg-linear-to-r from-cyan-400 via-blue-500 to-violet-600 dark:from-cyan-300 dark:via-blue-400 dark:to-violet-500">
              Full-Stack Engineering
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed"
          >
            From custom AI workflows to scalable web platforms: senior hands-on engineering designed to ship fast and grow your business.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="flex flex-wrap gap-4 items-center"
          >
            <Button asChild size="lg" className="rounded-full px-8 h-14 text-base font-medium bg-cyan-500 text-white hover:bg-cyan-400 transition-all duration-300 shadow-[0_0_30px_rgba(6,182,212,0.4)] border-none">
              <Link href="#methodology">
                Our Delivery Process
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-full px-8 h-14 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors">
              <Link href="#services-grid" className="flex items-center gap-2">
                View Services <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-cyan-500/50 text-[10px] tracking-[0.3em] uppercase"
      >
        <span>Scroll to Explore</span>
        <div className="w-px h-12 bg-linear-to-b from-cyan-500/50 to-transparent" />
      </motion.div>

      {/* Decorative Grid Lines */}
      <div className="absolute inset-0 z-1 pointer-events-none overflow-hidden opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          color: 'var(--foreground)'
        }} />
      </div>
    </div>
  )
}
